import { AnalyticIdentifier, createError } from '@common/neo.tax';
import { BaseTab, colors, Loading, Stack, Typography, useTabState } from '@ui-components/neo.tax';

import {
  isPayrollQreWithPayrollExpense,
  useRdTaxCreditContributor,
  usePayrollExpense,
  PayrollQre,
} from '@neo.tax/rd/hooks';
import { useAnalyticId, useCurrentUserScopeForCurrentCompany } from '@neo.tax/shared';

import { PayrollExpenseDetailsForm } from '../payroll-expense-details-form';
import { PayrollExpenseGeneralForm } from '../payroll-expense-general-form';
import { Container, BaseTabPanelStyled, BaseTabsStyled, TabPanelContainerStyled } from './payroll-qre-detail.styled';

type PayrollQreDetailProps = {
  expense: PayrollQre;
};

export const PayrollQreDetail = ({ expense: payrollQre }: PayrollQreDetailProps): React.ReactElement => {
  const generalTabId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_GENERAL_TAB);
  const detailTabId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_PAYROLL_QRE_TABLE_DETAIL_DETAILS_TAB);
  const tabState = useTabState({ defaultSelectedId: generalTabId });
  const { limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();

  const { loading: rdTaxCreditContributorLoading, rdTaxCreditContributor } = useRdTaxCreditContributor(
    payrollQre.rdTaxCreditContributorId,
  );
  const { loading: payrollExpenseLoading, payrollExpense } = usePayrollExpense(payrollQre.payrollExpenseId);

  // https://linear.app/neo-tax/issue/NEO-6221/determine-what-to-do-with-officiers-on-credit-calculation-page
  // https://linear.app/neo-tax/issue/NEO-6217/fix-check-type-issue-with-randd-changes
  // The incoming qre is coming from graphql query getPayrollExpense field `payrollQre`
  // This is a shared type between PayrollQre and PayrollExpense which makes payrollExpenseId optional
  // We need to check if the payroll QRE has a payroll expense, and if not, throw an error
  // Note, in PayrollQreLayout, this component is not rendered if the payroll QRE has no payroll expense
  // so in theory this code should never be reached.
  // It's simply here to enforce the type WithPayrollExpense<PayrollQre> further down the line
  if (!isPayrollQreWithPayrollExpense(payrollQre)) {
    throw createError('MissingPayrollExpense', 'Payroll QRE has no payroll expense');
  }

  return (
    <Container direction="column" overflow="hidden" backgroundColor={colors.gray[50]} fullHeight>
      {rdTaxCreditContributorLoading || payrollExpenseLoading || !rdTaxCreditContributor || !payrollExpense ? (
        <Loading height={300} />
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="start"
            justifyContent="space-between"
            paddingX={6}
            paddingY={2.5}
            backgroundColor={colors.gray[200]}
          >
            <Typography fontWeight="medium" fontSize="lg">
              {payrollQre.name}
            </Typography>
          </Stack>
          <BaseTabsStyled state={tabState}>
            <BaseTab label="General" tabId={generalTabId} />
            {!limitAccessToFinancialData ? <BaseTab label="Details" tabId={detailTabId} /> : <></>}
          </BaseTabsStyled>

          <TabPanelContainerStyled>
            <BaseTabPanelStyled state={tabState} tabId={generalTabId}>
              <PayrollExpenseGeneralForm
                payrollExpense={payrollExpense}
                rdTaxCreditContributor={rdTaxCreditContributor}
              />
            </BaseTabPanelStyled>
            {!limitAccessToFinancialData ? (
              <BaseTabPanelStyled state={tabState} tabId={detailTabId}>
                <PayrollExpenseDetailsForm payrollQre={payrollQre} />
              </BaseTabPanelStyled>
            ) : (
              <></>
            )}
          </TabPanelContainerStyled>
        </>
      )}
    </Container>
  );
};
